import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { isSupabaseConfigured, supabase } from "@/services/supabase-client.service";
import {
    getConversationsByTab,
    getMessages,
    sendMessage,
    markAsRead,
    updateRouting,
    updateRoutingWithAudit,
    updateWorkflowStatus,
    updateConversationStatus,
    getInboxStats,
} from "@/services/communication.service";
import type { Conversation, Message, InboxTab, RoutingTarget } from "@/types/communication";
import { useToast } from "@/components/shared/Toast";

// --- Query Keys ---
export const inboxKeys = {
    all: ["inbox"] as const,
    conversations: (practiceId?: string, tab?: InboxTab) =>
        [...inboxKeys.all, "conversations", practiceId, tab].filter(Boolean),
    messages: (conversationId?: string) =>
        [...inboxKeys.all, "messages", conversationId].filter(Boolean),
    stats: (practiceId?: string) => [...inboxKeys.all, "stats", practiceId] as const,
};

// --- Realtime Subscription Hook ---
export function useInboxRealtime(practiceId?: string) {
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!practiceId || !isSupabaseConfigured()) return;

        const channel = supabase
            .channel(`inbox-realtime-${practiceId}`)
            .on(
                "postgres_changes",
                {
                    event: "*",
                    schema: "public",
                    table: "conversations",
                    // NOTE: Realistically we need to listen where practice_id=practiceId OR practice_user_id=practiceId.
                    // Supabase postgres_changes doesn't support OR filters in client.
                    // We listen without filter and filter manually, or just trigger invalidate aggressively if less scale concerns.
                    // For practice web, practiceId is usually the user's practiceId. We'll listen without filter and check payload if possible, or just listen to all and invalidate.
                    // Actually, let's keep it simple: invalidate all inbox queries on any conversation change. (Supabase doesn't support complex OR filters for realtime).
                },
                () => {
                    queryClient.invalidateQueries({
                        queryKey: inboxKeys.conversations(practiceId),
                    });
                    queryClient.invalidateQueries({ queryKey: inboxKeys.stats(practiceId) });
                },
            )
            .on(
                "postgres_changes",
                {
                    event: "*",
                    schema: "public",
                    table: "messages",
                },
                (payload) => {
                    queryClient.invalidateQueries({
                        queryKey: inboxKeys.conversations(practiceId),
                    });
                    queryClient.invalidateQueries({ queryKey: inboxKeys.stats(practiceId) });

                    const newMsg = payload.new as any;
                    if (newMsg?.conversation_id) {
                        queryClient.invalidateQueries({
                            queryKey: inboxKeys.messages(newMsg.conversation_id),
                        });
                    }
                },
            )
            .subscribe();

        return () => {
            void supabase.removeChannel(channel);
        };
    }, [practiceId, queryClient]);
}

// --- Queries ---
export function useInboxStatsQuery(practiceId?: string) {
    return useQuery({
        queryKey: inboxKeys.stats(practiceId),
        queryFn: () => getInboxStats(practiceId!),
        enabled: !!practiceId,
    });
}

export function useConversationsQuery(practiceId?: string, tab: InboxTab = "all") {
    return useQuery({
        queryKey: inboxKeys.conversations(practiceId, tab),
        queryFn: () => getConversationsByTab(practiceId!, tab),
        enabled: !!practiceId,
    });
}

export function useMessagesQuery(conversationId?: string) {
    return useQuery({
        queryKey: inboxKeys.messages(conversationId),
        queryFn: () => getMessages(conversationId!),
        enabled: !!conversationId,
    });
}

// --- Mutations with Optimistic Updates ---
export function useSendMessageMutation(conversationId: string) {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    return useMutation({
        mutationFn: (input: {
            senderId: string;
            content: string;
            senderType?: string;
            senderName?: string;
            type?: Message["type"];
            metadata?: Record<string, any>;
        }) => sendMessage(conversationId, input),
        onMutate: async (newMsgInput) => {
            await queryClient.cancelQueries({ queryKey: inboxKeys.messages(conversationId) });
            const previousMessages = queryClient.getQueryData<Message[]>(
                inboxKeys.messages(conversationId),
            );

            if (previousMessages) {
                const optimisticMsg: Message = {
                    id: `temp-${Date.now()}`,
                    conversationId,
                    senderId: newMsgInput.senderId,
                    senderType: (newMsgInput.senderType as Message["senderType"]) || "practice",
                    senderName: newMsgInput.senderName || "Praxis",
                    type: newMsgInput.type || "text",
                    content: newMsgInput.content,
                    metadata: newMsgInput.metadata,
                    status: "sent",
                    createdAt: new Date().toISOString(),
                };
                queryClient.setQueryData<Message[]>(inboxKeys.messages(conversationId), (old) => [
                    ...(old || []),
                    optimisticMsg,
                ]);
            }
            return { previousMessages };
        },
        onError: (_err, _variables, context) => {
            if (context?.previousMessages) {
                queryClient.setQueryData(
                    inboxKeys.messages(conversationId),
                    context.previousMessages,
                );
            }
            addToast({ type: "error", title: "Nachricht senden fehlgeschlagen" });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: inboxKeys.messages(conversationId) });
            queryClient.invalidateQueries({ queryKey: inboxKeys.all }); // Refresh conv list & stats
        },
    });
}

export function useMarkAsReadMutation(practiceId?: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (conversationId: string) => markAsRead(conversationId),
        onMutate: async (conversationId) => {
            if (!practiceId) return {};
            await queryClient.cancelQueries({ queryKey: inboxKeys.conversations(practiceId) });
            await queryClient.cancelQueries({ queryKey: inboxKeys.stats(practiceId) });

            const previousConvs = queryClient.getQueriesData<Conversation[]>({
                queryKey: inboxKeys.conversations(practiceId),
            });

            // Optimistic update of unread counter in conversation list
            queryClient.setQueriesData<Conversation[]>(
                { queryKey: inboxKeys.conversations(practiceId) },
                (oldData) => {
                    if (!oldData) return oldData;
                    return oldData.map((c) =>
                        c.id === conversationId ? { ...c, unreadByPractice: 0, unread: false } : c,
                    );
                },
            );

            return { previousConvs };
        },
        onError: (_err, _variables, context: any) => {
            if (practiceId && context?.previousConvs) {
                context.previousConvs.forEach(([key, data]: [any, any]) => {
                    queryClient.setQueryData(key, data);
                });
            }
        },
        onSettled: () => {
            if (practiceId) {
                queryClient.invalidateQueries({ queryKey: inboxKeys.all });
            }
        },
    });
}

export function useUpdateRoutingMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    return useMutation({
        mutationFn: ({
            conversationId,
            target,
        }: {
            conversationId: string;
            target: Conversation["routingTarget"];
        }) => updateRouting(conversationId, target),
        onMutate: async ({ conversationId, target }) => {
            if (!practiceId) return {};
            await queryClient.cancelQueries({ queryKey: inboxKeys.conversations(practiceId) });

            const previousConvs = queryClient.getQueriesData<Conversation[]>({
                queryKey: inboxKeys.conversations(practiceId),
            });

            queryClient.setQueriesData<Conversation[]>(
                { queryKey: inboxKeys.conversations(practiceId) },
                (oldData) => {
                    if (!oldData) return oldData;
                    return oldData.map((c) =>
                        c.id === conversationId ? { ...c, routingTarget: target } : c,
                    );
                },
            );

            return { previousConvs };
        },
        onError: (_err, _variables, context: any) => {
            if (practiceId && context?.previousConvs) {
                context.previousConvs.forEach(([key, data]: [any, any]) =>
                    queryClient.setQueryData(key, data),
                );
            }
            addToast({ type: "error", title: "Weiterleiten fehlgeschlagen" });
        },
        onSettled: () => {
            if (practiceId) queryClient.invalidateQueries({ queryKey: inboxKeys.all });
        },
    });
}

export function useUpdateRoutingWithAuditMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    return useMutation({
        mutationFn: ({
            conversationId,
            target,
            actionUser,
            actionUserName,
            options,
        }: {
            conversationId: string;
            target: RoutingTarget;
            actionUser: string;
            actionUserName: string;
            options?: { internalNote?: string };
        }) => updateRoutingWithAudit(conversationId, target, actionUser, actionUserName, options),
        onMutate: async ({ conversationId, target }) => {
            if (!practiceId) return {};
            await queryClient.cancelQueries({ queryKey: inboxKeys.conversations(practiceId) });

            const previousConvs = queryClient.getQueriesData<Conversation[]>({
                queryKey: inboxKeys.conversations(practiceId),
            });

            queryClient.setQueriesData<Conversation[]>(
                { queryKey: inboxKeys.conversations(practiceId) },
                (oldData) => {
                    if (!oldData) return oldData;
                    return oldData.map((c) =>
                        c.id === conversationId ? { ...c, routingTarget: target } : c,
                    );
                },
            );

            return { previousConvs };
        },
        onError: (_err, _variables, context: any) => {
            if (practiceId && context?.previousConvs) {
                context.previousConvs.forEach(([key, data]: [any, any]) =>
                    queryClient.setQueryData(key, data),
                );
            }
            addToast({ type: "error", title: "Weiterleiten fehlgeschlagen" });
        },
        onSettled: () => {
            if (practiceId) queryClient.invalidateQueries({ queryKey: inboxKeys.all });
        },
    });
}

export function useUpdateWorkflowStatusMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    return useMutation({
        mutationFn: ({
            conversationId,
            status,
        }: {
            conversationId: string;
            status: Conversation["workflowStatus"];
        }) => updateWorkflowStatus(conversationId, status),
        onMutate: async ({ conversationId, status }) => {
            if (!practiceId) return {};
            await queryClient.cancelQueries({ queryKey: inboxKeys.conversations(practiceId) });

            const previousConvs = queryClient.getQueriesData<Conversation[]>({
                queryKey: inboxKeys.conversations(practiceId),
            });

            queryClient.setQueriesData<Conversation[]>(
                { queryKey: inboxKeys.conversations(practiceId) },
                (oldData) => {
                    if (!oldData) return oldData;
                    return oldData.map((c) =>
                        c.id === conversationId ? { ...c, workflowStatus: status } : c,
                    );
                },
            );

            return { previousConvs };
        },
        onError: (_err, _variables, context: any) => {
            if (practiceId && context?.previousConvs) {
                context.previousConvs.forEach(([key, data]: [any, any]) =>
                    queryClient.setQueryData(key, data),
                );
            }
            addToast({ type: "error", title: "Status aktualisieren fehlgeschlagen" });
        },
        onSettled: () => {
            if (practiceId) queryClient.invalidateQueries({ queryKey: inboxKeys.all });
        },
    });
}

export function useUpdateConversationStatusMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    return useMutation({
        mutationFn: ({
            conversationId,
            status,
        }: {
            conversationId: string;
            status: Conversation["status"];
        }) => updateConversationStatus(conversationId, status),
        onMutate: async ({ conversationId, status }) => {
            if (!practiceId) return {};
            await queryClient.cancelQueries({ queryKey: inboxKeys.conversations(practiceId) });

            const previousConvs = queryClient.getQueriesData<Conversation[]>({
                queryKey: inboxKeys.conversations(practiceId),
            });

            queryClient.setQueriesData<Conversation[]>(
                { queryKey: inboxKeys.conversations(practiceId) },
                (oldData) => {
                    if (!oldData) return oldData;
                    return oldData.map((c) => (c.id === conversationId ? { ...c, status } : c));
                },
            );

            return { previousConvs };
        },
        onError: (_err, _variables, context: any) => {
            if (practiceId && context?.previousConvs) {
                context.previousConvs.forEach(([key, data]: [any, any]) =>
                    queryClient.setQueryData(key, data),
                );
            }
            addToast({ type: "error", title: "Status ändern fehlgeschlagen" });
        },
        onSettled: () => {
            if (practiceId) queryClient.invalidateQueries({ queryKey: inboxKeys.all });
        },
    });
}
